import { z } from 'zod'

export const PROMPT_CATEGORIES = [
  'copywriting',
  'coding',
  'content',
  'email',
  'social',
  'seo',
  'research',
  'analysis',
  'translation',
  'education',
  'marketing',
  'sales',
  'customer-support',
  'creative',
  'other',
] as const

// MODELS: the prompt creator previously offered only stale models
// (Claude 3 / ChatGPT-4) while the agent wizard offered the current generation.
// `AI_MODEL_OPTIONS` is the current set shown in the picker; `AI_MODELS` is the
// superset accepted by validation (current + legacy) so prompts created against
// the old list still validate and remain editable.
export const AI_MODEL_OPTIONS = [
  'claude-sonnet-4-6',
  'claude-opus-4-6',
  'claude-haiku-4-5',
  'chatgpt-4o',
  'gemini-2-pro',
  'gemini-2-flash',
] as const

export const AI_MODELS = [
  // current (offered as options)
  'claude-sonnet-4-6',
  'claude-opus-4-6',
  'claude-haiku-4-5',
  'chatgpt-4o',
  'gemini-2-pro',
  'gemini-2-flash',
  // legacy (back-compat only — accepted, not offered as new options)
  'claude-3-sonnet',
  'claude-3-opus',
  'chatgpt-4',
  'gemini-pro',
] as const

/**
 * Prompt visibility states (D3.3 - aligned with Product Definition.md L180-183).
 * Ordered private → unlisted → public, ascending by reach. The DB CHECK
 * constraint accepts these three exact values; an explicit `draft` state is
 * intentionally absent because the spec defines `private` as the draft
 * surface ("Private - doar tu îl vezi. Ca un draft.").
 */
export const VISIBILITY_OPTIONS = ['private', 'unlisted', 'public'] as const

/** Per-state human label, capitalised for direct display. */
export const VISIBILITY_LABELS: Record<(typeof VISIBILITY_OPTIONS)[number], string> = {
  private: 'Private',
  unlisted: 'Unlisted',
  public: 'Public',
}

/** Per-state description used in the visibility picker (spec copy). */
export const VISIBILITY_DESCRIPTIONS: Record<(typeof VISIBILITY_OPTIONS)[number], string> = {
  private: 'Only you can see it. Use this for drafts or work in progress.',
  unlisted: 'Not searchable. Anyone with the link can view it.',
  public: 'Appears in feed, search, and the community graph.',
}

export const promptFormSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Title is required')
    .max(200, '200 characters max'),
  readmeContent: z
    .string()
    .max(5000, '5000 characters max'),
  promptContent: z
    .string()
    .trim()
    .min(1, 'Prompt content is required')
    .max(10000, '10,000 characters max'),
  category: z.enum(PROMPT_CATEGORIES, {
    message: 'Category is required',
  }),
  visibility: z.enum(VISIBILITY_OPTIONS),
  tags: z
    .array(z.string().max(20, 'Tag must be 20 characters or less'))
    .max(10, 'Maximum 10 tags'),
  aiModel: z
    .array(z.enum(AI_MODELS))
    .min(1, 'Select at least one AI model'),
})

export type PromptFormInput = z.infer<typeof promptFormSchema>
